<template>
  <Card class="relative">
    <CardHeader class="flex flex-row items-center justify-between">
      <CardTitle>{{ t('dashboard.shared.programProgress.members') }}</CardTitle>
      <button
        v-if="isUserMentor && selectedGroup"
        data-test-id="group-dropdown-options"
        :title="t('dashboard.shared.programProgress.groupSettings')"
        @click="openGroupSettings"
      >
        <SettingsIcon class="size-4" />
      </button>
    </CardHeader>
    <CardContent>
      <div v-if="selectedGroup" class="flex flex-col gap-2">
        <DropdownMenu v-for="user in filteredUsers" :key="user.id">
          <DropdownMenuTrigger as-child>
            <div
              class="flex items-center gap-3 rounded-md p-2 cursor-pointer hover:bg-muted transition-colors"
              :data-test-id="`user-card-${user.id}`"
            >
              <ShadcnAvatar class="size-10 shrink-0">
                <AvatarImage v-if="user.picture" :src="PROFILE_PICTURE_BUCKET_URL + user.picture" :alt="`${user.firstName} ${user.lastName}`" />
                <AvatarFallback>{{ user.firstName?.[0] }}{{ user.lastName?.[0] }}</AvatarFallback>
              </ShadcnAvatar>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium truncate">{{ user.firstName }} {{ user.lastName }}</div>
                <div class="text-xs text-muted-foreground">{{ getRoleLabel(user.roles, user.id) }}</div>
              </div>
              <EllipsisVerticalIcon data-test-id="more-options-group" class="size-4 shrink-0" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-test-id="more-actions-menu" @click="openUserProfile(user.id)">
              <UserIcon class="size-4" />
              {{ t('moreActions.user.seeProfile') }}
            </DropdownMenuItem>
            <DropdownMenuItem v-if="user.id !== accountRef?.id" data-test-id="more-actions-menu" @click="sendMessage(user.id)">
              <MessageSquareIcon class="size-4" />
              {{ t('moreActions.user.sendMessage') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div v-else class="flex flex-col gap-3 py-2">
        <div v-for="i in 3" :key="i" class="flex items-center gap-3">
          <Skeleton class="size-10 rounded-full shrink-0" />
          <div class="flex flex-col gap-1.5 flex-1">
            <Skeleton class="h-3.5 w-3/5" />
            <Skeleton class="h-3 w-2/5" />
          </div>
        </div>
      </div>
      <LockedOverlay
        v-if="userGroups.length === 0"
        :message="t('dashboard.shared.lockedContent.noGroup')"
      />
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { EllipsisVerticalIcon, MessageSquareIcon, SettingsIcon, UserIcon } from 'lucide-vue-next'

import type { V2GroupType } from '@/features/groups/types'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useAccount from '@/features/auth/hooks/use-account.hook'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import { Avatar as ShadcnAvatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { PROFILE_PICTURE_BUCKET_URL, ROUTES } from '@/utils/config/constants'
import LockedOverlay from '@/features/ui/components/locked-overlay/locked-overlay.vue'
import type { GroupSettingsModalType, UserProfileModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const router = useRouter()
const { accountRef } = useAccount()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

const props = defineProps<{
  userGroups: V2GroupType[]
  selectedGroup: V2GroupType | undefined
}>()

const { groupRolesRef } = useGroupRoles({ groupIdRef: computed(() => props.selectedGroup?.id ?? '') })
const isUserMentor = computed(() => groupRolesRef.value?.some((r) => r === RoleEnumTypeV2.MENTOR) ?? false)

const filteredUsers = computed(() => {
  if (!props.selectedGroup) return []
  return props.selectedGroup.users.filter((user) =>
    user.roles.some((role) => role === RoleEnumTypeV2.MENTOR || role === RoleEnumTypeV2.MENTEE)
  )
})

function getRoleLabel(roles: RoleEnumTypeV2[], userId: string): string {
  let label = ''

  if (roles.includes(RoleEnumTypeV2.MENTOR)) {
    label = t('dashboard.shared.programProgress.mentor')
  } else if (roles.includes(RoleEnumTypeV2.MENTEE)) {
    label = t('dashboard.shared.programProgress.mentee')
  }

  if (userId === accountRef.value?.id) {
    label += ` (${t('dashboard.shared.programProgress.me')})`
  }

  return label
}

function openGroupSettings() {
  if (!props.selectedGroup) return
  modalsStore.toggleModal('group-settings', true)
  modalsStore.updateModalAttachedData<GroupSettingsModalType>('group-settings', { groupId: props.selectedGroup.id })
}

function openUserProfile(userId: string) {
  modalsStore.toggleModal('user-profile', true)
  modalsStore.updateModalAttachedData<UserProfileModalType>('user-profile', { userId })
}

function sendMessage(userId: string) {
  if (!selectedProgramIdRef.value) return
  router.push(`${ROUTES.CHAT()}?sendTo=${userId}&programId=${selectedProgramIdRef.value}`)
}
</script>
